//----------------------------------------------------------------------------------------------------------------------
// Logger Utility
//----------------------------------------------------------------------------------------------------------------------

import type {
    LogLevel,
    LoggerInterface,
    LoggingBackend,
} from '../interfaces/logger.ts';

import { ConsoleBackend } from '../classes/loggers/consoleBackend.ts';
import { NullBackend } from '../classes/loggers/nullBackend.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Implementation of Logger interface.
 * Each instance is bound to a specific category and backend.
 */
export class SAGELogger implements LoggerInterface
{
    private category : string;
    private backend : LoggingBackend;
    private minLevel : LogLevel;

    constructor(category : string, minLevel : LogLevel = 'none', backend : LoggingBackend = new NullBackend())
    {
        this.category = category;
        this.backend = backend;
        this.minLevel = minLevel;
    }

    /**
     * Update the logger's backend and minimum level
     */
    public updateSettings(backend : LoggingBackend, minLevel : LogLevel) : void
    {
        this.backend = backend;
        this.minLevel = minLevel;
    }

    trace(message : string, ...args : unknown[]) : void
    {
        if(this.shouldLog('trace'))
        {
            this.backend.trace(this.category, message, ...args);
        }
    }

    debug(message : string, ...args : unknown[]) : void
    {
        if(this.shouldLog('debug'))
        {
            this.backend.debug(this.category, message, ...args);
        }
    }

    info(message : string, ...args : unknown[]) : void
    {
        if(this.shouldLog('info'))
        {
            this.backend.info(this.category, message, ...args);
        }
    }

    warn(message : string, ...args : unknown[]) : void
    {
        if(this.shouldLog('warn'))
        {
            this.backend.warn(this.category, message, ...args);
        }
    }

    error(message : string, ...args : unknown[]) : void
    {
        if(this.shouldLog('error'))
        {
            this.backend.error(this.category, message, ...args);
        }
    }

    time(label : string) : void
    {
        if(this.minLevel !== 'none')
        {
            this.backend.time(this.category, label);
        }
    }

    timeEnd(label : string) : void
    {
        if(this.minLevel !== 'none')
        {
            this.backend.timeEnd(this.category, label);
        }
    }

    /**
     * Determine if a message at the given level should be logged based on the current minimum level.
     */
    private shouldLog(level : LogLevel) : boolean
    {
        if(this.minLevel === 'none')
        {
            return false;
        }

        switch (this.minLevel)
        {
            case 'trace':
                return true;
            case 'debug':
                return level !== 'trace';
            case 'info':
                return level === 'info' || level === 'warn' || level === 'error';
            case 'warn':
                return level === 'warn' || level === 'error';
            case 'error':
                return level === 'error';
            default:
                return false;
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * A utility for creating loggers and managing logging configuration.
 */
export class LoggingUtility
{
    private backend : LoggingBackend;
    private level : LogLevel;
    private loggers : Map<string, LoggerInterface>;

    /**
     * Create a new LoggingUtility.
     *
     * @param level - The minimum logging level (defaults to INFO)
     * @param backend - The logging backend to use (defaults to ConsoleBackend)
     */
    constructor(level : LogLevel = 'debug', backend : LoggingBackend = new ConsoleBackend())
    {
        this.backend = backend;
        this.level = level;
        this.loggers = new Map();
    }

    /**
     * Set the logging backend. Updates all existing loggers to use the new backend.
     *
     * @param backend
     */
    public setBackend(backend : LoggingBackend) : void
    {
        this.backend = backend;

        // Update all existing loggers to use the new backend
        this.loggers.forEach((logger) =>
        {
            if(logger instanceof SAGELogger)
            {
                logger.updateSettings(this.backend, this.level);
            }
        });
    }

    /**
     * Set the minimum logging level. Updates all existing loggers.
     *
     * @param level
     */
    public setLevel(level : LogLevel) : void
    {
        this.level = level;

        // Update all existing loggers to use the new level
        this.loggers.forEach((logger) =>
        {
            if(logger instanceof SAGELogger)
            {
                logger.updateSettings(this.backend, this.level);
            }
        });
    }

    /**
     * Get the current minimum logging level.
     */
    public getLevel() : LogLevel
    {
        return this.level;
    }

    /**
     * Get or create a logger for the specified category. Returns an existing logger if one was already created.
     *
     * @param category - Typically the class or module name
     */
    public getLogger(category : string) : LoggerInterface
    {
        let logger = this.loggers.get(category);
        if(!logger)
        {
            logger = new SAGELogger(category, this.level, this.backend);
            this.loggers.set(category, logger);
        }

        return logger;
    }
}

//----------------------------------------------------------------------------------------------------------------------

export { ConsoleBackend } from '../classes/loggers/consoleBackend.ts';
export { NullBackend } from '../classes/loggers/nullBackend.ts';

//----------------------------------------------------------------------------------------------------------------------
